/**
 * components/tabs/RiskLimitsTable.jsx
 *
 * upstox-073 — Settings → Account → Risk limits, as designed (the Claude Design handoff): ONE table;
 * rows are the nine parameters in three groups, columns are the accounts the user may see. One
 * column is edited at a time. The server's behaviour rules (HANDOVER 2026-10-08, upstox-073):
 *   - a save always creates a NEW version (max + 1); the first save for an account applies at once,
 *     later ones from the next session not yet started. There is no "update the pending version":
 *     the label says "Save as vN" and, with a pending version, that it replaces it.
 *   - the server enforces 0 < amber < red < limit (and more: timers, the EOD time, …); the page
 *     shows the design's ordering warning and holds the Save, and leaves every other refusal to
 *     the server, shown in its own words.
 *   - the date of the next session is the server's; the page names it only when a pending version
 *     already carries it, and otherwise after the save, from the server's answer.
 * Every request is exactly what the page sent before: GET / POST /api/settings/upstox/risk
 * (with ?account=<id> on a Kite account, none on the user's own Upstox) and the same body.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { withAccount, fetchAccounts } from '../riskTerminal/accounts';
import { apiFetch } from './settingsApi';
import { Pill, MONO } from './SettingsParts';

// Versioned, fixed rupee amounts (upstox-020). Before the first save NOTHING is in force: the
// column opens on the agreed starting values, marked as suggested, never treated as settings.
export const RISK_STARTING_VALUES = {
  daily_loss_limit: 30000, amber_loss_amount: 20000, red_loss_amount: 25000,
  timer_amber_minutes: 10, timer_red_minutes: 20, reclassify_window_minutes: 10,
  end_of_day_check_time: '15:00', rationale_min_chars: 40, neutral_ratio_cutoff: 0.20,
};

/** The nine parameters: `label` is the validation message's wording (unchanged), `name` the row's. */
export const RISK_FIELDS = [
  { g: 'Loss thresholds', key: 'daily_loss_limit',          name: 'Daily loss limit',      label: 'Daily loss limit (₹)',        kind: 'rupees', pre: '₹', hint: 'Trading stops at this loss' },
  { g: 'Loss thresholds', key: 'amber_loss_amount',         name: 'Amber warning at',      label: 'Amber warning at a loss of (₹)', kind: 'rupees', pre: '₹' },
  { g: 'Loss thresholds', key: 'red_loss_amount',           name: 'Red warning at',        label: 'Red warning at a loss of (₹)',   kind: 'rupees', pre: '₹' },
  { g: 'Timers',          key: 'timer_amber_minutes',       name: 'Against-trend · amber', label: 'Against-trend timer — amber (min)', kind: 'int', suf: 'min' },
  { g: 'Timers',          key: 'timer_red_minutes',         name: 'Against-trend · red',   label: 'Against-trend timer — red (min)',   kind: 'int', suf: 'min' },
  { g: 'Timers',          key: 'reclassify_window_minutes', name: 'Reclassify window',     label: 'Reclassify window (min)',     kind: 'int', suf: 'min' },
  { g: 'Discipline',      key: 'end_of_day_check_time',     name: 'End-of-day check',      label: 'End-of-day check (IST, HH:MM)', kind: 'time', suf: 'IST' },
  { g: 'Discipline',      key: 'rationale_min_chars',       name: 'Minimum rationale',     label: 'Minimum rationale (characters)', kind: 'int', suf: 'chars' },
  { g: 'Discipline',      key: 'neutral_ratio_cutoff',      name: 'Neutral cutoff',        label: 'Neutral cutoff (|net Δ| ÷ gross, 0–1)', kind: 'decimal', hint: '|net Δ| ÷ gross, 0–1' },
];
const GROUP_NOTE = { 'Loss thresholds': 'Losses for the day, in rupees.', Timers: 'How long an against-trend position can run.', Discipline: 'Checks on how trades are logged.' };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-10-09" → "9 Oct" */
export function shortDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : String(iso || '');
}

/** "7 Oct 2026, 09:30" — the server's IST time, read as written (no browser-zone shift). */
export function lastChangedText(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || '');
  if (!m) return String(iso || '');
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}, ${m[4]}:${m[5]}`;
}

/** The body as JSON, or null when it is not JSON (a plain-text 500). */
export async function readJson(r) {
  try { return JSON.parse(await r.text()); } catch { return null; }
}

/** The server's refusal in words (a string, a validation list), else its status. */
export function refusalText(status, d) {
  const detail = d && d.detail;
  if (Array.isArray(detail)) return detail.map(e => `${(e.loc || []).slice(-1)[0]}: ${e.msg}`).join('; ');
  if (typeof detail === 'string' && detail) return detail;
  return `The server refused this (HTTP ${status}) without a message; nothing was saved.`;
}

const lossText = n => `₹${Number(n).toLocaleString('en-IN')}`;

/** How a value reads in a cell: ₹30,000 · 10 min · 15:00 IST · 40 chars · 0.2 */
export function fmtValue(f, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (f.kind === 'rupees') return lossText(v);
  if (f.suf === 'min') return `${v} min`;
  if (f.suf) return `${v} ${f.suf}`;
  return String(v);
}

/** The values a column opens on: the pending version, else the one in force, else the suggestions. */
export function baseValues(data) {
  const v = (data && (data.pending || data.in_force)) || RISK_STARTING_VALUES;
  const out = {};
  RISK_FIELDS.forEach(({ key }) => { out[key] = String(v[key] ?? RISK_STARTING_VALUES[key]); });
  return out;
}

/** What the Save button says — by what the server does (a new version each time). */
export function saveLabelFor(data) {
  if (!data || (!data.in_force && !data.pending)) return 'Save & apply now';
  const n = Math.max(data.in_force ? data.in_force.version : 0, data.pending ? data.pending.version : 0) + 1;
  return `Save as v${n}`;
}

/** The design's ordering rule — the one the server enforces (0 < amber < red < limit): the rest is the server's to say. */
export function orderingInvalid(d) {
  const am = Number(d.amber_loss_amount), rd = Number(d.red_loss_amount), lim = Number(d.daily_loss_limit);
  return !(am < rd && rd < lim);
}

function statusOf(data) {
  if (!data) return { kind: 'quiet', text: '…' };
  if (data.pending) return { kind: 'warn', text: `v${data.pending.version} pending · ${shortDay(data.pending.effective_session_date)}` };
  if (data.in_force) return { kind: 'ok', text: `v${data.in_force.version} in force` };
  return { kind: 'bad', text: 'Not set' };
}

const cellBase = { minWidth: 0, borderLeft: '1px solid var(--hair)' };

export default function RiskLimitsTable({ listAccounts = fetchAccounts, initial }) {
  const [accts, setAccts] = useState(null);
  const [datas, setDatas] = useState({});               // account key -> the server's read
  const [errs, setErrs] = useState({});
  const [editing, setEditing] = useState((initial && initial.editing) || null);
  const [drafts, setDrafts] = useState((initial && initial.drafts) || {});
  const [note, setNote] = useState((initial && initial.note) || null);      // the last save's outcome: { key, text, ok }
  const [saving, setSaving] = useState(false);

  const keyOf = a => (a.broker === 'UPSTOX' ? 'upstox' : String(a.id));
  const idOf = a => (a.broker === 'UPSTOX' ? null : a.id);                  // the Upstox column sends no ?account=

  useEffect(() => {
    let alive = true;
    listAccounts().then(r => {
      if (!alive) return;
      const list = r.ok ? r.data.accounts : [];
      // the list failing leaves the user's own Upstox column, as the page always had
      setAccts(list.length ? list : [{ id: null, broker: 'UPSTOX', label: 'Upstox' }]);
    });
    return () => { alive = false; };
  }, [listAccounts]);

  const load = useCallback((a) =>
    apiFetch(withAccount('/api/settings/upstox/risk', idOf(a)))
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setDatas(s => ({ ...s, [keyOf(a)]: d })); setErrs(s => ({ ...s, [keyOf(a)]: '' })); })
      .catch(() => setErrs(s => ({ ...s, [keyOf(a)]: 'Could not load risk settings.' }))), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { (accts || []).forEach(a => { if (!(initial && initial.datas)) load(a); }); }, [accts, load]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (initial && initial.datas) setDatas(initial.datas); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cols = accts || [];
  const edit = cols.find(a => keyOf(a) === editing) || null;
  const ek = edit ? keyOf(edit) : null;
  const eData = ek ? datas[ek] : null;
  const eBase = eData ? baseValues(eData) : null;
  const eDraft = ek && eBase ? (drafts[ek] || eBase) : null;
  const changed = f => !!eDraft && String(eDraft[f.key]) !== String(eBase[f.key]);
  const nChanges = eDraft ? RISK_FIELDS.filter(changed).length : 0;
  const eNew = !!eData && !eData.in_force && !eData.pending;
  const invalid = !!eDraft && orderingInvalid(eDraft);
  const canSave = !!eDraft && !saving && !invalid && (eNew || nChanges > 0);

  const startEdit = a => { setEditing(keyOf(a)); setNote(null); };
  const cancel = () => { setDrafts(d => { const n = { ...d }; delete n[ek]; return n; }); setEditing(null); };
  const setDraft = (f, v) => setDrafts(d => ({ ...d, [ek]: { ...(d[ek] || eBase), [f.key]: v } }));

  const handleSave = async () => {
    const body = {};
    for (const { key, kind, label: text } of RISK_FIELDS) {
      const raw = String(eDraft[key] ?? '').trim();
      if (kind === 'time') { body[key] = raw; continue; }
      const n = Number(raw);
      if (raw === '' || !Number.isFinite(n)) { setNote({ key: ek, text: `Enter a number for "${text}".`, ok: false }); return; }
      body[key] = n;   // range and whole-number rules are the server's, returned as 422
    }
    setSaving(true); setNote(null);
    let r;
    try {
      r = await apiFetch(withAccount('/api/settings/upstox/risk', idOf(edit)), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch { setNote({ key: ek, text: 'Network error — no answer from the server; nothing was saved.', ok: false }); setSaving(false); return; }
    try {
      // upstox-069: an answer is never "Network error" — the server's words, or its status
      const d = await readJson(r);
      if (!r.ok) setNote({ key: ek, text: refusalText(r.status, d), ok: false });
      else {
        setNote({ key: ek, ok: true, text: d.takes_effect === 'now'
          ? `✓ ${edit.label} saved as version ${d.saved.version} — in force now.`
          : `✓ ${edit.label} saved as version ${d.saved.version} — takes effect from the next session, ${d.saved.effective_session_date}.` });
        setDrafts(x => { const n = { ...x }; delete n[ek]; return n; });
        setEditing(null);
        load(edit);
      }
    } catch { setNote({ key: ek, text: `The server answered (HTTP ${r.status}) but the answer could not be read; nothing was confirmed saved.`, ok: false }); }
    finally { setSaving(false); }
  };

  const verParts = [];
  if (eData) {
    if (eData.in_force) verParts.push(`v${eData.in_force.version} in force since ${shortDay(eData.in_force.effective_session_date)}`);
    if (eData.pending) verParts.push(`v${eData.pending.version} pending from ${shortDay(eData.pending.effective_session_date)}`);
    if (eNew) verParts.push('Nothing in force. Suggested starting values; nothing applies until you save');
    if (eData.last_changed) verParts.push(`last changed by ${eData.last_changed.by}, ${lastChangedText(eData.last_changed.at)}`);
  }
  const rows = [];
  let g = null;
  RISK_FIELDS.forEach(f => { if (f.g !== g) { g = f.g; rows.push({ group: g }); } rows.push({ f }); });

  return (
    <div data-risk-table>
      <div style={{ border: '1px solid var(--hair)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' }}>
        <div className="sx2-scroll">
          <div style={{ minWidth: 150 + cols.length * 150 }}>
            {/* header: the accounts */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--hair)', background: 'var(--surface-2)' }}>
              <div style={{ flex: 1.3, minWidth: 0, padding: '12px 16px', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-2)', alignSelf: 'flex-end' }}>Parameter</div>
              {cols.map(a => {
                const k = keyOf(a), d = datas[k], st = statusOf(d), isEd = ek === k;
                return (
                  <div key={k} data-account-col={a.label} style={{ ...cellBase, flex: 1, padding: '10px 16px 12px', display: 'flex', flexDirection: 'column', gap: 6,
                    ...(isEd ? { background: 'var(--surface)', boxShadow: 'inset 0 2px 0 var(--amber-ink)' } : {}) }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 24 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{a.label}</span>
                      <span style={{ flex: 1 }} />
                      {isEd
                        ? <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: 'var(--amber-ink)' }}>EDITING</span>
                        : <button type="button" className="sx2-link" data-edit={a.label} disabled={!d} onClick={() => startEdit(a)}>{d && !d.in_force ? 'Set up' : 'Edit'}</button>}
                    </div>
                    <div style={{ display: 'flex' }}><Pill kind={st.kind} small>{st.text}</Pill></div>
                    {errs[k] && <div style={{ fontSize: 11, color: 'var(--down-field-ink)' }}>{errs[k]}</div>}
                  </div>
                );
              })}
            </div>
            {/* the nine parameters, in three groups */}
            {rows.map((r, i) => r.group ? (
              <div key={'g' + i} title={GROUP_NOTE[r.group]} style={{ padding: '14px 16px 6px', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber-ink)', fontWeight: 600 }}>{r.group}</div>
            ) : (
              <div key={r.f.key} style={{ display: 'flex', fontSize: 13, borderTop: '1px solid var(--hair)' }}>
                <div style={{ flex: 1.3, minWidth: 0, padding: '9px 16px', color: 'var(--ink-2)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                  <span>{r.f.name}</span>
                  {r.f.hint && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.f.hint}</span>}
                </div>
                {cols.map(a => {
                  const k = keyOf(a), d = datas[k];
                  if (k === ek && eDraft) {
                    const ch = changed(r.f);
                    return (
                      <div key={k} data-cell={`${r.f.key}:${a.label}`} style={{ ...cellBase, flex: 1, padding: '5px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3, background: 'var(--surface)' }}>
                        <div className="sx2-wrap">
                          {r.f.pre && <span style={{ paddingLeft: 9, color: 'var(--ink-2)', fontFamily: MONO, fontSize: 12.5 }}>{r.f.pre}</span>}
                          <input data-risk-input={r.f.key} value={eDraft[r.f.key]} inputMode={r.f.kind === 'time' ? 'text' : r.f.kind === 'decimal' ? 'decimal' : 'numeric'}
                            onChange={e => setDraft(r.f, e.target.value)}
                            style={{ flex: 1, minWidth: 0, height: '100%', border: 'none', outline: 'none', padding: '0 8px 0 5px', fontFamily: MONO, fontSize: 12.5, background: 'transparent', color: 'var(--ink)' }} />
                          {r.f.suf && <span style={{ padding: '0 9px', color: 'var(--ink-2)', fontSize: 11, whiteSpace: 'nowrap' }}>{r.f.suf}</span>}
                        </div>
                        {ch && <span data-was style={{ fontSize: 11, color: 'var(--amber-ink)' }}>● was {fmtValue(r.f, eBase[r.f.key])}</span>}
                      </div>
                    );
                  }
                  const now = d && d.in_force ? d.in_force[r.f.key] : null;
                  const next = d && d.pending && (!d.in_force || String(d.pending[r.f.key]) !== String(d.in_force[r.f.key])) ? d.pending[r.f.key] : null;
                  return (
                    <div key={k} data-cell={`${r.f.key}:${a.label}`} style={{ ...cellBase, flex: 1, padding: '9px 16px', fontFamily: MONO, fontSize: 12.5, display: 'flex', flexWrap: 'wrap', gap: '6px 8px', alignItems: 'center' }}>
                      {now == null && next == null && <span style={{ color: 'var(--ink-4)' }}>—</span>}
                      {now != null && <span>{fmtValue(r.f, now)}</span>}
                      {next != null && <span data-next style={{ color: 'var(--amber-ink)', background: 'var(--amber-bg)', padding: '0 5px', borderRadius: 3 }}>→ {fmtValue(r.f, next)}</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        {/* footer: the column being edited, or the legend */}
        {edit && eDraft ? (
          <div data-risk-footer style={{ padding: '14px 16px', borderTop: '1px solid var(--hair)', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-2)' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{edit.label}</span><span>{verParts.join(' · ')}</span>
            </div>
            {invalid && <span data-ordering style={{ fontSize: 12, color: 'var(--down-field-ink)' }}>Amber has to be below red, and red below the daily limit.</span>}
            {note && note.key === ek && !note.ok && <div data-risk-msg style={{ fontSize: 12, color: 'var(--down-field-ink)' }}>{note.text}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{eNew && !nChanges ? 'First save' : nChanges + (nChanges === 1 ? ' change' : ' changes')}</span>
              <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                {eNew ? 'Applies immediately.'
                  : `Applies from the next session${eData.pending ? ` (${shortDay(eData.pending.effective_session_date)})` : ' that has not started'}, never mid-session.${eData.pending ? ` Replaces pending v${eData.pending.version}.` : ''}`}
              </span>
              <span style={{ flex: 1 }} />
              <button type="button" className="sx2-ghost" onClick={cancel}>Cancel</button>
              <button type="button" className="sx2-primary" data-risk-save disabled={!canSave} onClick={handleSave}>{saving ? 'Saving…' : saveLabelFor(eData)}</button>
            </div>
          </div>
        ) : (
          <div data-risk-legend style={{ padding: '10px 16px', borderTop: '1px solid var(--hair)', fontSize: 12, color: 'var(--ink-2)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--amber-ink)', background: 'var(--amber-bg)', padding: '0 5px', borderRadius: 3, fontFamily: MONO }}>→ value</span>
            pending, takes effect from the next session. Press Edit on an account to change its column.
          </div>
        )}
      </div>
      {!edit && note && (
        <div data-risk-msg style={{ marginTop: 10, fontSize: 12, color: note.ok ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>{note.text}</div>
      )}
    </div>
  );
}
